import SpektrixAPI from "../utils/SpektrixAPI";

class SpektrixSession {
    constructor() {
        this.api = new SpektrixAPI();
        this.loadingIndicator = document.querySelector(
            ".article__loading-indicator",
        );
        this.keepingAlive = false;

        window.addEventListener("message", this.receiveMessage.bind(this));
        document.addEventListener("click", this.handleClick.bind(this));
        document.addEventListener(
            "submit",
            this.loginFormSubmission.bind(this),
        );
    }

    /**
     * Compare the local WP session identity against the live Spektrix-side
     * session.
     *
     * The WP-side identity comes from `ajax_client.spektrix_session_email`,
     * which is server-rendered into the page from the (HttpOnly)
     * spektrix_session cookie via wp_localize_script in Enqueue.php. The
     * cookie itself is no longer JS-readable (M-THEME-2-FULL).
     *
     * The previous implementation could re-mint the WP cookie based on
     * Spektrix's `/customer` response. That path is removed (H-THEME-1):
     * minting now requires server-side re-authentication via the WP AJAX
     * endpoint, which needs a password the browser does not have at this
     * point in the flow. We can only act on the *clear* case — if Spektrix
     * is logged out but we still hold a cookie, log them out locally.
     */
    checkSession() {
        const localEmail = (typeof ajax_client !== "undefined" && ajax_client.spektrix_session_email)
            ? ajax_client.spektrix_session_email
            : false;

        this.api.request("/customer", "GET").then((response) => {
            if (!response || response.errorCode) {
                console.error("Error in response from /customer:", response);
                return;
            }

            const spektrixEmail = response.email ?? false;

            if (!spektrixEmail && localEmail) {
                // Spektrix has no logged-in customer but we still hold a
                // cookie — clear it locally to keep state in sync.
                this.clearSession(false);
                return;
            }

            if (spektrixEmail && localEmail === spektrixEmail) {
                this.keepAlive();
            }

            // Otherwise (no local cookie, or identity mismatch): no
            // client-side action. The user must re-authenticate via the WP
            // login form for the WP-side session to update. Note: because
            // ajax_client.spektrix_session_email is computed at page render
            // time, login flow finishes with location.reload() so the
            // localised value is fresh on the next page.
        });
    }

    // ASVS V3.5.5: act only on messages from the Spektrix box office iframe. Any page can
    // post to this window; the trusted senders are the configured box office origin and
    // the origin of a Spektrix iframe this page actually embeds (the plugin's
    // spektrix_domain setting, so staging and a domain change keep working).
    receiveMessage(e) {
        if (!SpektrixSession.isSpektrixOrigin(e.origin)) return;
        if (typeof e.data !== "string") return;

        if (
            e.data.includes("basketHash:NotLoggedIn") ||
            e.data.includes("basketHash:LoggedIn")
        ) {
            this.checkSession();
        }
    }

    /**
     * Authenticated session creation. Sends email + password + nonce to the
     * WP AJAX endpoint, which re-authenticates against Spektrix server-side
     * via the System Owner credentials and mints the WP session cookie.
     */
    createSession({ email, password }) {
        const params = new URLSearchParams();
        params.append("action", "spektrix_session");
        params.append("nonce", ajax_client.spektrix_session_nonce);
        params.append("email", email);
        params.append("password", password);

        return fetch(ajax_client.ajax_url, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
            body: params.toString(),
        }).then((response) => response.json());
    }

    /**
     * Clear the WP session cookie via the AJAX endpoint. Nonce-guarded.
     */
    clearSession(redirect = false, setLoadingState = false) {
        if (setLoadingState) this.setLoadingState(true);

        const params = new URLSearchParams();
        params.append("action", "spektrix_session");
        params.append("nonce", ajax_client.spektrix_session_nonce);

        return fetch(ajax_client.ajax_url, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
            body: params.toString(),
        })
            .then((response) => response.json())
            .then(() => {
                if (redirect) {
                    const url = new URL(location.href);

                    if (url.searchParams.get("r")) {
                        location.href = url.searchParams.get("r");
                    } else {
                        location.reload();
                    }
                }
            })
            .catch((error) => {
                console.error("Error in clearSession:", error);
                this.setLoadingState(false);
            });
    }

    logOut() {
        this.setLoadingState(true);

        // End the Spektrix-side session first (browser-side), then clear the
        // WP-side cookie. Order matters: if we clear ours first and the
        // Spektrix call fails, the user is left in an inconsistent state.
        this.api
            .request("/customer/deauthenticate", "POST", 1)
            .then(() => {
                this.clearSession(true, true);
            })
            .catch((error) => {
                console.error("Error logging out:", error);
                this.setLoadingState(false);
            });
    }

    /**
     * Login flow.
     *
     * 1. Authenticate browser-side against Spektrix to set Spektrix's session
     *    cookie (needed by the box-office iframe and other Spektrix-direct UI).
     * 2. Re-authenticate server-side via the WP AJAX endpoint, which mints
     *    the WP session cookie. The WP endpoint enforces nonce + server-side
     *    Spektrix re-auth — anyone bypassing step 1 cannot reach step 2.
     */
    login(data, redirect) {
        const validation = document.querySelector(".account-login__validation");
        const errors = document.querySelector(".account-login__errors");

        errors.textContent = "";
        validation.hidden = true;

        this.api
            .request("/customer/authenticate", "POST", data)
            .then((spektrixResponse) => {
                if (!spektrixResponse || spektrixResponse.errorCode) {
                    errors.textContent =
                        "Login failed. Please check your email address and password and try again.";
                    validation.hidden = false;
                    this.setLoadingState(false);
                    return;
                }

                this.createSession(data)
                    .then((wpResponse) => {
                        if (!wpResponse || !wpResponse.logged_in) {
                            errors.textContent =
                                "Login succeeded but session could not be established. Please try again.";
                            validation.hidden = false;
                            this.setLoadingState(false);
                            return;
                        }

                        if (redirect) {
                            const url = new URL(location.href);

                            if (url.searchParams.get("r")) {
                                location.href = url.searchParams.get("r");
                            } else {
                                location.reload();
                            }
                        }
                    })
                    .catch((error) => {
                        console.error("Error in createSession:", error);
                        errors.textContent =
                            "Login failed. Please try again.";
                        validation.hidden = false;
                        this.setLoadingState(false);
                    });
            });
    }

    loginFormSubmission(e) {
        if (e.target.id !== "login-form") {
            return;
        }

        e.preventDefault();

        this.setLoadingState(true);

        const form = e.target;
        const formData = new FormData(form);

        const data = {
            email: formData.get("email-address"),
            password: formData.get("password"),
        };

        this.login(data, true);
    }

    /**
     * Keep the session alive by re-validating against Spektrix every 60s.
     * Only triggered after a confirmed identity match in checkSession().
     */
    keepAlive() {
        if (this.keepingAlive) {
            return;
        }

        setInterval(() => {
            console.log("Keeping Spektrix session alive...");

            this.checkSession();
        }, 60000);

        this.keepingAlive = true;
    }

    setLoadingState(loading = false) {
        this.loadingIndicator &&
            this.loadingIndicator.setAttribute("active", loading);
    }

    handleClick(e) {
        if (e.target.getAttribute("data-action") === "logout") {
            e.preventDefault();

            console.log("Logging out...");

            this.logOut();
        }
    }

    static isSpektrixOrigin(origin) {
        if (!origin) return false;
        if (SpektrixSession.SPEKTRIX_ORIGINS.includes(origin)) return true;
        return Array.from(document.querySelectorAll("iframe[data-iframe-uid]")).some((f) => {
            const src = f.getAttribute("src") || f.getAttribute("data-src");
            if (!src) return false;
            try {
                return new URL(src, window.location.href).origin === origin;
            } catch {
                return false;
            }
        });
    }
}

// The box office iframe's origin on www (read from the live basket page, 2026-10-26).
SpektrixSession.SPEKTRIX_ORIGINS = ["https://boxoffice.arcolatheatre.com"];

export default SpektrixSession;
